import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Lock, User, Upload, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [logoUrl, setLogoUrl] = useState(localStorage.getItem('companyLogo') || '');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: 'Arquivo muito grande',
          description: 'A logo deve ter no máximo 2MB.',
          variant: 'destructive',
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setLogoUrl(base64String);
        localStorage.setItem('companyLogo', base64String);
        toast({
          title: 'Logo atualizada!',
          description: 'A logo da empresa foi carregada com sucesso.',
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Simular delay de autenticação
    setTimeout(() => {
      if (credentials.username === 'DGQ' && credentials.password === 'elohim') {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('username', credentials.username);
        toast({
          title: 'Login realizado!',
          description: `Bem-vindo de volta, ${credentials.username}!`,
        });
        onLogin();
      } else {
        toast({
          title: 'Credenciais inválidas',
          description: 'Usuário ou senha incorretos. Tente novamente.',
          variant: 'destructive',
        });
        setIsLoading(false);
      }
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8">
          {logoUrl ? (
            <div className="flex flex-col items-center gap-4">
              <img
                src={logoUrl}
                alt="Logo da Empresa"
                className="h-24 w-auto object-contain"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => document.getElementById('logo-upload').click()}
                className="text-slate-600 hover:text-slate-900"
              >
                <Upload className="w-4 h-4 mr-2" />
                Alterar Logo
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Shield className="w-12 h-12 text-white" />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('logo-upload').click()}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Adicionar Logo da Empresa
              </Button>
            </div>
          )}
          <input
            id="logo-upload"
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            className="hidden"
          />
        </div>

        {/* Login Card */}
        <Card className="shadow-2xl border-orange-200">
          <CardHeader className="space-y-1 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-200">
            <CardTitle className="text-2xl font-bold text-center text-slate-900">
              Sistema de Gestão de Instrumentos
            </CardTitle>
            <CardDescription className="text-center text-slate-600">
              Entre com suas credenciais para acessar o sistema
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-700 font-medium">
                  Usuário
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="username"
                    type="text"
                    value={credentials.username}
                    onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                    placeholder="Digite seu usuário"
                    className="pl-10 border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    placeholder="Digite sua senha"
                    className="pl-10 border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                    required
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-orange-800">
                  <p className="font-medium">Acesso restrito</p>
                  <p className="text-orange-700 mt-1">
                    Apenas usuários autorizados podem acessar este sistema.
                  </p>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-medium py-6 text-lg shadow-lg hover:shadow-xl transition-all"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Autenticando...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Entrar no Sistema
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-slate-600">
          <p>© 2025 Sistema de Gestão de Instrumentos</p>
          <p className="mt-1">Todos os direitos reservados</p>
        </div>
      </div>
    </div>
  );
};

export default Login;